import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { ErrorBox } from '@common/components';
import CheckmarkIcon from '@common/components/icons/CheckmarkIcon';
import { fetchAvailableTables, validateQuery } from '@api/dataSource';
import { getTimestampColumns } from '@reports/utils/dashboard/columnUtils';
import * as multiSelectStyles from '@common/components/MultiSelectList/MultiSelectList.module.css';
import {
  DATA_SOURCE_TABS,
  DATA_SOURCE_TYPES,
  DEFAULT_TABLE_CONFIG,
  DEFAULT_METRIC_VALUES_CONFIG,
  DEFAULT_INVENTORY_CONFIG,
  DEFAULT_CUSTOM_QUERY_CONFIG,
  MAX_LINE_CHART_DATA_SOURCES,
  HIDDEN_DATA_SOURCES,
} from './constants';
import * as styles from './Step2DataSource.module.css';
import * as wizardStyles from './styles.module.css';

const QUERY_DEBOUNCE_MS = 500;

function Step2DataSource({ widgetData, onConfigChange, onColumnsUpdate, validationErrors }) {
  const { t } = useTranslation();
  const [availableTables, setAvailableTables] = useState([]);
  const [tablesWithTimeColumns, setTablesWithTimeColumns] = useState(new Set());
  const [isLoadingTables, setIsLoadingTables] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [queryError, setQueryError] = useState(null);
  const [lineChartNoTimestampWarning, setLineChartNoTimestampWarning] = useState(false);
  const [isValidatingQuery, setIsValidatingQuery] = useState(false);

  // Get dataSources from config
  const dataSources = widgetData.config.dataSources || [];

  // Determine active tab based on whether we have a custom query
  const hasCustomQuery = dataSources.some((ds) => ds.type === DATA_SOURCE_TYPES.CUSTOM_QUERY);
  const activeTab = hasCustomQuery ? 'custom_query' : 'table_columns';

  // Get current custom query for debounced validation
  const currentQuery = dataSources.find((ds) => ds.type === DATA_SOURCE_TYPES.CUSTOM_QUERY)?.config?.query || '';

  // Check if widget type is line chart (only line charts can use metric_values)
  const isLineChart = widgetData.type === 'line';
  // Check if widget type is table (only tables can use inventory)
  const isTableWidget = widgetData.type === 'table';

  // Load available tables/views from API
  // Uses hasTimeColumn flag from backend when available, falls back to column check
  const loadAvailableTables = useCallback(async () => {
    setIsLoadingTables(true);
    try {
      const response = await fetchAvailableTables();
      if (response.status === 'success') {
        const tables = response.data;
        setAvailableTables(tables);

        // Build set of tables with time columns using backend flag
        const tablesWithTime = new Set();
        tables.forEach((table) => {
          if (table.hasTimeColumn) {
            tablesWithTime.add(table.name);
          }
        });
        setTablesWithTimeColumns(tablesWithTime);
      } else {
        setAvailableTables([]);
        setTablesWithTimeColumns(new Set());
      }
    } catch {
      setAvailableTables([]);
      setTablesWithTimeColumns(new Set());
    } finally {
      setIsLoadingTables(false);
    }
  }, []);

  useEffect(() => {
    loadAvailableTables();
  }, [loadAvailableTables]);

  // Sync selectedIds from dataSources config
  useEffect(() => {
    const currentDataSources = widgetData.config.dataSources || [];
    const ids = currentDataSources.map((ds) => ds.id);

    setSelectedIds((prevIds) => {
      const prevStr = JSON.stringify([...prevIds].sort());
      const newStr = JSON.stringify([...ids].sort());
      return prevStr === newStr ? prevIds : ids;
    });
  }, [widgetData.config.dataSources]);

  // Handle tab change (Entity Selection vs Custom Query)
  const handleTabChange = useCallback(
    (tabValue) => {
      if (tabValue === 'custom_query') {
        // Switch to custom query mode - config inside dataSource
        onConfigChange('dataSources', [
          {
            id: 'custom_query',
            type: DATA_SOURCE_TYPES.CUSTOM_QUERY,
            config: { ...DEFAULT_CUSTOM_QUERY_CONFIG },
          },
        ]);
        setSelectedIds([]);
      } else {
        // Switch to entity selection mode
        onConfigChange('dataSources', []);
        setSelectedIds([]);
      }
    },
    [onConfigChange],
  );

  // Handle entity selection change (multi-select) - builds dataSources array with config
  const handleEntitySelectionChange = useCallback(
    (newSelectedIds) => {
      setSelectedIds(newSelectedIds);

      const hasInventory = newSelectedIds.includes('inventory');
      const hasMetricValues = newSelectedIds.includes('metric_values');
      const tableIds = newSelectedIds.filter((id) => id !== 'metric_values' && id !== 'inventory');

      // Build dataSources array with type-specific config inside each source
      const newDataSources = [];

      // Handle inventory selection (exclusive - can't combine with other sources)
      if (hasInventory) {
        newDataSources.push({
          id: 'inventory',
          type: DATA_SOURCE_TYPES.INVENTORY,
          config: { ...DEFAULT_INVENTORY_CONFIG },
        });
        onConfigChange('dataSources', newDataSources);
        if (onColumnsUpdate) onColumnsUpdate([]);
        return;
      }

      // Add metric_values if selected - with config inside
      if (hasMetricValues) {
        newDataSources.push({
          id: 'metric_values',
          type: DATA_SOURCE_TYPES.METRIC_VALUES,
          config: { ...DEFAULT_METRIC_VALUES_CONFIG },
        });
      }

      // Add tables - with config inside
      tableIds.forEach((tableId) => {
        newDataSources.push({
          id: tableId,
          type: DATA_SOURCE_TYPES.TABLE,
          config: { ...DEFAULT_TABLE_CONFIG },
        });
      });

      onConfigChange('dataSources', newDataSources);
    },
    [onConfigChange, onColumnsUpdate],
  );

  // Handle query change for custom query - updates config inside dataSource
  const handleQueryChange = useCallback(
    (query) => {
      // Update query inside the custom_query dataSource's config immediately
      const currentDataSources = widgetData.config.dataSources || [];
      const updatedDataSources = currentDataSources.map((ds) => {
        if (ds.type === DATA_SOURCE_TYPES.CUSTOM_QUERY) {
          return {
            ...ds,
            config: { ...ds.config, query },
          };
        }
        return ds;
      });
      onConfigChange('dataSources', updatedDataSources);

      // Clear error when user starts typing
      setQueryError(null);
    },
    [onConfigChange, widgetData.config.dataSources],
  );

  // Debounced query validation effect - only depends on currentQuery
  useEffect(() => {
    // Skip if not in custom query mode
    if (!hasCustomQuery) {
      return undefined;
    }

    // Handle empty query
    if (!currentQuery || !currentQuery.trim()) {
      if (onColumnsUpdate) onColumnsUpdate([]);
      setLineChartNoTimestampWarning(false);
      setIsValidatingQuery(false);
      return undefined;
    }

    // Set validating state and start debounce timer
    setIsValidatingQuery(true);
    const timeoutId = setTimeout(async () => {
      try {
        const response = await validateQuery(currentQuery);
        if (response.status === 'success' && response.data?.columns) {
          const { columns } = response.data;
          if (onColumnsUpdate) onColumnsUpdate(columns);
          setQueryError(null);
          // Line chart requires at least one timestamp column (API returns { name, type }[])
          if (isLineChart) {
            const timestampCols = getTimestampColumns(columns);
            setLineChartNoTimestampWarning(timestampCols.length === 0);
          } else {
            setLineChartNoTimestampWarning(false);
          }
        } else {
          // Error can be in response.data.error (validation errors) or response.message (network/other errors)
          const errorMessage = response.data?.error || response.message || 'Failed to validate query';
          setQueryError(errorMessage);
          if (onColumnsUpdate) onColumnsUpdate([]);
          setLineChartNoTimestampWarning(false);
        }
      } catch (error) {
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to validate query';
        setQueryError(errorMessage);
        if (onColumnsUpdate) onColumnsUpdate([]);
        setLineChartNoTimestampWarning(false);
      } finally {
        setIsValidatingQuery(false);
      }
    }, QUERY_DEBOUNCE_MS);

    // Only cleanup on unmount or when currentQuery changes
    return () => clearTimeout(timeoutId);
  }, [currentQuery, hasCustomQuery, isLineChart, onColumnsUpdate]);

  // Prepare items for MultiSelectList - only show tables with time columns for line charts
  const entityItems = useMemo(() => {
    const items = [];

    // Add regular tables/views - for line charts, only include tables with time columns
    availableTables.forEach((table) => {
      // Skip internal tables unless already selected (MON-17323)
      // This allows editing existing widgets that may have used these tables before hiding
      const isHidden = HIDDEN_DATA_SOURCES.includes(table.name);
      if (isHidden && !selectedIds.includes(table.name)) {
        return;
      }

      // Skip tables without time columns for line charts
      if (isLineChart && !tablesWithTimeColumns.has(table.name)) {
        return;
      }

      items.push({
        id: table.name,
        name: `${table.name} (${table.type.toUpperCase()})`,
        description: `${table.database || 'reporting db'} - ${table.description || 'Database table or view'}`,
        type: DATA_SOURCE_TYPES.TABLE,
      });
    });

    // Add metric values option (only for line charts)
    if (isLineChart) {
      items.push({
        id: 'metric_values',
        name: t('dashboard.widgetWizard.step2.metricValuesLabel'),
        description: t('dashboard.widgetWizard.step2.metricValuesDescription'),
        type: DATA_SOURCE_TYPES.METRIC_VALUES,
      });
    }

    // Add inventory option (only for table widgets)
    if (isTableWidget) {
      items.push({
        id: 'inventory',
        name: t('dashboard.widgetWizard.inventory.title'),
        description: t('dashboard.widgetWizard.inventory.description'),
        type: DATA_SOURCE_TYPES.INVENTORY,
      });
    }

    return items;
  }, [availableTables, tablesWithTimeColumns, isLineChart, isTableWidget, t, selectedIds]);

  return (
    <div className={styles.container}>
      <h2 className={wizardStyles.stepTitle}>{t('dashboard.widgetWizard.step2.title')}</h2>

      {/* Error Box - Show all errors at top */}
      {validationErrors.dataSource && <ErrorBox errors={[validationErrors.dataSource]} />}

      {/* Data Source Method Tabs */}
      <div className={styles.tabSection}>
        <p className={styles.sectionLabel}>{t('dashboard.widgetWizard.step2.dataSourceMethod')}</p>
        <div className={styles.tabContainer}>
          <div className={styles.tabButtons}>
            {DATA_SOURCE_TABS.map((tab) => (
              <button
                key={tab.value}
                type="button"
                className={`${styles.tabButton} ${activeTab === tab.value ? styles.active : ''}`}
                onClick={() => handleTabChange(tab.value)}
              >
                {t(tab.label)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Entity Selection Tab Content */}
      {activeTab === 'table_columns' && (
        <div className={styles.entitySection}>
          <div className={styles.labelRow}>
            <label className={multiSelectStyles.label} htmlFor="entity-selection">
              {t('dashboard.widgetWizard.step2.selectEntity')}
              {isLineChart && <span className={styles.limitText}> (up to {MAX_LINE_CHART_DATA_SOURCES})</span>}
            </label>
            {isLineChart && <span className={styles.counterBadge}>{selectedIds.length}/{MAX_LINE_CHART_DATA_SOURCES} Selected</span>}
          </div>

          {/* Selection List */}
          <div className={multiSelectStyles.list} style={{ maxHeight: '422px' }}>
            {isLoadingTables && (
              <div className={multiSelectStyles.loading}>{t('dashboard.widgetWizard.step2.loadingEntities')}</div>
            )}

            {!isLoadingTables && entityItems.length === 0 && (
              <div className={multiSelectStyles.empty}>{t('dashboard.widgetWizard.step2.loadingEntities')}</div>
            )}

            {!isLoadingTables &&
              entityItems.map((item) => {
                const isSelected = selectedIds.includes(item.id);
                const isDisabled = isLineChart && !isSelected && selectedIds.length >= MAX_LINE_CHART_DATA_SOURCES;
                const itemClassName = isLineChart && isDisabled ? styles.disabled : '';
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={`${multiSelectStyles.item} ${isSelected ? multiSelectStyles.selected : ''} ${itemClassName}`}
                    onClick={() => {
                      let newSelection;
                      if (isLineChart) {
                        // Multi-select for line charts (max MAX_LINE_CHART_DATA_SOURCES)
                        if (isSelected) {
                          // Remove if already selected
                          newSelection = selectedIds.filter((id) => id !== item.id);
                        } else if (selectedIds.length < MAX_LINE_CHART_DATA_SOURCES) {
                          // Add if under limit
                          newSelection = [...selectedIds, item.id];
                        } else {
                          // Already at limit, don't change
                          return;
                        }
                      } else {
                        // Single-select for other chart types
                        newSelection = isSelected ? [] : [item.id];
                      }
                      handleEntitySelectionChange(newSelection);
                    }}
                    disabled={isDisabled}
                  >
                    {isLineChart && (
                      <span className={multiSelectStyles.checkboxContainer}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}}
                          className={multiSelectStyles.customCheckbox}
                          tabIndex={-1}
                          aria-hidden="true"
                          disabled={!isSelected && selectedIds.length >= MAX_LINE_CHART_DATA_SOURCES}
                        />
                        {isSelected && (
                          <span className={multiSelectStyles.checkboxIconContainer}>
                            <CheckmarkIcon />
                          </span>
                        )}
                      </span>
                    )}
                    <div className={multiSelectStyles.itemContent} style={!isLineChart ? { marginLeft: 0 } : {}}>
                      <span className={multiSelectStyles.itemLabel}>{item.name}</span>
                      {item.description && (
                        <span className={multiSelectStyles.itemDescription}>{item.description}</span>
                      )}
                    </div>
                  </button>
                );
              })}
          </div>
        </div>
      )}

      {/* Custom Query Tab Content */}
      {activeTab === 'custom_query' && (
        <div className={styles.querySection}>
          <p className={styles.sectionLabel}>{t('dashboard.widgetWizard.step2.customQueryLabel')}</p>
          {isLineChart && (
            <p className={styles.helpTextSpaced}>
              {t('dashboard.widgetWizard.step2.lineChartTimestampRequired')}
            </p>
          )}
          {queryError && <ErrorBox errors={[queryError]} />}
          {lineChartNoTimestampWarning && !queryError && (
            <ErrorBox errors={[t('dashboard.widgetWizard.step2.lineChartTimestampRequired')]} />
          )}
          <textarea
            className={`${styles.queryTextarea} ${queryError ? styles.queryTextareaError : ''}`}
            value={currentQuery}
            onChange={(e) => handleQueryChange(e.target.value)}
            placeholder={t('dashboard.widgetWizard.step2.customQueryPlaceholder')}
            rows={10}
          />
          {isValidatingQuery && (
            <p className={styles.validatingText}>{t('dashboard.widgetWizard.step2.validatingQuery')}</p>
          )}
          <p className={styles.helpText}>{t('dashboard.widgetWizard.step2.customQueryHelp')}</p>
        </div>
      )}
    </div>
  );
}

Step2DataSource.propTypes = {
  widgetData: PropTypes.shape({
    type: PropTypes.string,
    config: PropTypes.shape({
      dataSources: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          type: PropTypes.string,
        }),
      ),
      query: PropTypes.string,
    }),
  }).isRequired,
  onConfigChange: PropTypes.func.isRequired,
  onColumnsUpdate: PropTypes.func,
  validationErrors: PropTypes.shape({
    dataSource: PropTypes.string,
  }).isRequired,
};

Step2DataSource.defaultProps = {
  onColumnsUpdate: null,
};

export default Step2DataSource;
